/**
 * 文档共享：把 PDF / 图片导入成白板页。
 *
 * 老系统靠 OLE 嵌入直接在客户端打开 PowerPoint（msppt9.cpp / msword9.cpp），
 * 因此**必须装 Office、必须是 Windows**，而且课件里的动画和字体在别人机器上
 * 经常错乱。新系统统一在浏览器里把每页栅格化成图片再上传，
 * 三端呈现一致，也不依赖任何本地软件。
 *
 * PPT/PPTX 不直接支持：浏览器里没有可靠的解析实现。
 * 请先另存为 PDF —— 这也是老系统实际使用中最常见的做法。
 */

import type { PDFDocumentProxy } from 'pdfjs-dist'

import { canvasToBlob } from './upload'
import { isSvgFile, sanitizeSvg, svgViewBox } from './svgAsset'

/**
 * 懒加载 pdf.js。
 *
 * 它压缩后有 400 多 KB，而绝大多数进教室的人从不导入课件 ——
 * 静态引入会让所有人都为这段代码付出首屏加载时间。
 */
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null

async function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((m) => {
      // worker 从依赖里取，避免走 CDN（内网部署没有外网）。
      m.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.mjs',
        import.meta.url,
      ).toString()
      return m
    })
  }
  return pdfjsPromise
}

/** 渲染出的一页。 */
export interface RenderedPage {
  index: number
  blob: Blob
  width: number
  height: number
  /** 'svg' 表示 blob 里是矢量源码，不能当位图画；缺省按位图处理 */
  kind?: 'image' | 'svg'
}

export interface ImportProgress {
  done: number
  total: number
  label: string
}

/** 单页栅格化的目标宽度。
 *
 * 1600px 是权衡后的取值：投影到教室大屏够清晰，
 * 单页 PNG 又能控制在几百 KB，30 页课件不至于把白板容量吃满。 */
const TARGET_WIDTH = 1600

/** 支持的文件类型。 */
export function isSupportedDoc(file: File): boolean {
  const n = file.name.toLowerCase()
  return (
    file.type === 'application/pdf' ||
    n.endsWith('.pdf') ||
    file.type.startsWith('image/')
  )
}

/** 判断是否为需要提示转换的 Office 文档。 */
export function isOfficeDoc(file: File): boolean {
  const n = file.name.toLowerCase()
  return /\.(ppt|pptx|doc|docx|xls|xlsx)$/.test(n)
}

/**
 * 把文档渲染成若干页图片。
 */
export async function renderDocument(
  file: File,
  onProgress?: (p: ImportProgress) => void,
  maxPages = 30,
): Promise<RenderedPage[]> {
  // 矢量图不栅格化，原样收下。
  //
  // 走 renderImage 的话就把矢量拍死在某个分辨率上了：浏览器给
  // 「只有 viewBox、没写宽高」的 SVG（Figma / Illustrator 的常见导出）
  // 报的固有尺寸只有 200×150，按这个栅格化再铺满一页，斜线全是台阶。
  if (isSvgFile(file)) {
    onProgress?.({ done: 0, total: 1, label: '读取矢量图' })
    const text = await file.text()
    const clean = sanitizeSvg(text)
    if (!clean.ok) throw new Error(clean.reason)
    const box = svgViewBox(clean.svg)
    onProgress?.({ done: 1, total: 1, label: '读取矢量图' })
    return [{
      index: 0,
      blob: new Blob([clean.svg], { type: 'image/svg+xml' }),
      width: box.w,
      height: box.h,
      kind: 'svg',
    }]
  }

  if (file.type.startsWith('image/')) {
    onProgress?.({ done: 0, total: 1, label: '处理图片' })
    const page = await renderImage(file)
    onProgress?.({ done: 1, total: 1, label: '处理图片' })
    return [page]
  }

  onProgress?.({ done: 0, total: 1, label: '正在载入 PDF 解析器…' })
  const pdfjs = await loadPdfjs()

  const buf = await file.arrayBuffer()
  const doc: PDFDocumentProxy = await pdfjs.getDocument({ data: buf }).promise

  // 白板页数有上限（对应老系统 MAXPAGENUM=30），超出的部分不导入，
  // 而不是导入一半再失败。
  const total = Math.min(doc.numPages, maxPages)
  const out: RenderedPage[] = []

  for (let i = 1; i <= total; i++) {
    onProgress?.({ done: i - 1, total, label: `渲染第 ${i} / ${total} 页` })

    const page = await doc.getPage(i)
    const base = page.getViewport({ scale: 1 })
    const viewport = page.getViewport({ scale: TARGET_WIDTH / base.width })

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(viewport.width)
    canvas.height = Math.round(viewport.height)
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('浏览器不支持 Canvas 2D')

    // 白底：PDF 页面本身透明，直接导出会得到黑底 PNG。
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    await page.render({ canvas, canvasContext: ctx, viewport }).promise
    out.push({
      index: i - 1,
      blob: await canvasToBlob(canvas),
      width: canvas.width,
      height: canvas.height,
    })
    page.cleanup()
  }

  onProgress?.({ done: total, total, label: '渲染完成' })
  doc.cleanup()
  return out
}

/** 把单张图片处理成一页。 */
async function renderImage(file: File): Promise<RenderedPage> {
  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const scale = Math.min(1, TARGET_WIDTH / img.naturalWidth)
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('浏览器不支持 Canvas 2D')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)

    return { index: 0, blob: await canvasToBlob(canvas), width: w, height: h }
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** 载入一张图片。 */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('图片解码失败'))
    img.src = url
  })
}

/**
 * 计算课件页图在白板上的摆放位置（归一化坐标）。
 *
 * 白板画布是 4:3，课件常见 16:9，直接铺满会变形。
 * 这里按比例居中放置，留白而不拉伸。
 */
export function fitBox(
  pageW: number,
  pageH: number,
  boardAspect = 4 / 3,
): { x0: number; y0: number; x1: number; y1: number } {
  const docAspect = pageW / pageH
  if (docAspect >= boardAspect) {
    // 文档更宽：横向铺满，纵向居中。
    const h = boardAspect / docAspect
    const y0 = (1 - h) / 2
    return { x0: 0, y0, x1: 1, y1: y0 + h }
  }
  // 文档更高：纵向铺满，横向居中。
  const w = docAspect / boardAspect
  const x0 = (1 - w) / 2
  return { x0, y0: 0, x1: x0 + w, y1: 1 }
}
